import type { PDFMergeFunction } from "@pdfking/renderer-api"
import {
  BlobToUint8,
  IterableToArray,
  Map as MapSync,
  Range,
  ToBlob,
} from "@pdfking/tfunc"
import type { PDFObject } from "pdf-lib"
import {
  PDFArray,
  PDFContext,
  PDFDict,
  PDFDocument,
  PDFHeader,
  PDFName,
  PDFObjectCopier,
  PDFRef,
} from "pdf-lib"

const toBytes = (obj: {
  sizeInBytes(): number
  copyBytesInto(buffer: Uint8Array, offset: number): number
}): Uint8Array => {
  const bytes = new Uint8Array(obj.sizeInBytes())
  obj.copyBytesInto(bytes, 0)
  return bytes
}

export const PDFMerge: PDFMergeFunction = async function* (pages) {
  const objectOffsets: Map<number, number> = new Map()
  let currentOffset = 0
  let nextObjNum = 1
  const pageRefs: Map<number, PDFRef> = new Map()

  const nextRef = () => PDFRef.of(nextObjNum++)

  function writeObjects(
    ...objects: [PDFRef, PDFObject][]
  ): (string | Uint8Array)[] {
    const footer = "\nendobj\n\n"
    const result = []
    for (const [{ objectNumber }, obj] of objects) {
      objectOffsets.set(objectNumber, currentOffset)
      const header = `${objectNumber} 0 obj\n`
      const objBytes = toBytes(obj)

      currentOffset += header.length + objBytes.length + footer.length
      result.push(header, objBytes, footer)
    }
    return result
  }

  // Reserve refs for Catalog and Pages (written at the end)
  const catalogRef = nextRef()
  const pagesRef = nextRef()

  // Output context for building final objects
  const outContext = PDFContext.create()

  // Yield PDF header
  const headerBytes = toBytes(PDFHeader.forVersion(1, 7))
  yield ToBlob([headerBytes, "\n"])
  currentOffset += headerBytes.length + 1

  for await (const { page_number, stream } of pages) {
    const srcDoc = await IterableToArray(stream)
      .then(ToBlob)
      .then(BlobToUint8)
      .then(bytes =>
        PDFDocument.load(bytes, {
          ignoreEncryption: true,
          updateMetadata: false,
        })
      )

    if (srcDoc.getPageCount() !== 1) {
      throw new Error(
        `Expected single-page PDF, got ${srcDoc.getPageCount()} pages`
      )
    }

    // Create a patched context for this page that uses our global ref counter
    const pageContext = PDFContext.create()
    pageContext.nextRef = nextRef

    // Copy the page and all its referenced objects into pageContext
    const copier = PDFObjectCopier.for(srcDoc.context, pageContext)
    const copy = copier.copy(srcDoc.getPage(0).node)

    // Set Parent to our Pages ref
    copy.set(PDFName.of("Parent"), pagesRef)

    // Track page ref with its target page number
    pageRefs.set(page_number, pageContext.register(copy))

    yield ToBlob(writeObjects(...pageContext.enumerateIndirectObjects()))
  }

  // Build Pages dict with Kids array in page number order
  const pagesDict = PDFDict.withContext(outContext)
  pagesDict.set(PDFName.of("Type"), PDFName.of("Pages"))
  const kidsArray = PDFArray.withContext(outContext)
  for (const i of Range(1, pageRefs.size)) {
    kidsArray.push(pageRefs.get(i)!)
  }
  pagesDict.set(PDFName.of("Kids"), kidsArray)
  pagesDict.set(PDFName.of("Count"), outContext.obj(pageRefs.size))

  // Write Catalog object (ref 1)
  const catalogDict = PDFDict.withContext(outContext)
  catalogDict.set(PDFName.of("Type"), PDFName.of("Catalog"))
  catalogDict.set(PDFName.of("Pages"), pagesRef)

  yield ToBlob(writeObjects([catalogRef, catalogDict], [pagesRef, pagesDict]))

  yield ToBlob([
    `xref\n0 ${nextObjNum}\n`,
    "0000000000 65535 f \n",
    ...MapSync(
      i => `${String(objectOffsets.get(i) ?? 0).padStart(10, "0")} 00000 n \n`,
      Range(1, nextObjNum - 1)
    ),
    `trailer\n<</Size ${nextObjNum} /Root ${catalogRef.objectNumber} 0 R>>\nstartxref\n${currentOffset}\n%%EOF\n`,
  ])
}
